import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Quote, Star, Send } from "lucide-react";
import Header from "@/components/Header";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestimonialsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    testimonial_text: "",
    full_name: "",
    email: "",
    country: ""
  });

  // Fetch approved testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials/approved`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  // Count words in testimonial
  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const wordCount = countWords(formData.testimonial_text);
  const isWordLimitExceeded = wordCount > 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isWordLimitExceeded) {
      toast.error("Testimonial exceeds 100 word limit");
      return;
    }

    if (!formData.testimonial_text || !formData.full_name || !formData.email || !formData.country) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("testimonial_text", formData.testimonial_text);
      formDataToSend.append("full_name", formData.full_name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("country", formData.country);

      await axios.post(`${API}/testimonials/submit`, formDataToSend);
      
      toast.success("Thank you! Your testimonial has been submitted for review.");
      
      // Reset form
      setFormData({
        testimonial_text: "",
        full_name: "",
        email: "",
        country: ""
      });
    } catch (error) {
      toast.error("Failed to submit testimonial. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Header />

      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Client Testimonials
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Hear what our clients say about their experience working with EviDent Research Consultancy
            </p>
          </div>

          {/* Testimonials Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading testimonials...</p>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No testimonials yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="p-6">
                    {/* Quote Icon */}
                    <div className="mb-4">
                      <Quote className="h-8 w-8 text-blue-600 opacity-50" />
                    </div>

                    {/* Rating */}
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, index) => (
                        <Star key={index} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>

                    {/* Testimonial Text */}
                    <p className="text-gray-700 text-base mb-6 italic leading-relaxed">
                      "{testimonial.testimonial_text}"
                    </p>

                    {/* Customer Details */}
                    <div className="border-t pt-4">
                      <p className="font-semibold text-gray-900 text-lg">{testimonial.full_name}</p>
                      <p className="text-sm text-gray-500">{testimonial.country}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(testimonial.created_at).toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Submit Testimonial Form */}
          <div className="max-w-3xl mx-auto mt-16">
            <Card className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                  <Send className="h-6 w-6 mr-2 text-blue-600" />
                  Share Your Experience
                </h2>
                <p className="text-gray-600 mb-6">
                  We'd love to hear about your experience working with us. Your testimonial will be reviewed before appearing on this page.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Testimonial Text */}
                  <div>
                    <Label htmlFor="testimonial_text" className="text-base font-semibold">
                      Your Testimonial <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="testimonial_text"
                      value={formData.testimonial_text}
                      onChange={(e) => setFormData({...formData, testimonial_text: e.target.value})}
                      placeholder="Share your experience working with EviDent Research Consultancy..."
                      className="mt-2 min-h-[150px]"
                      required
                    />
                    <p className={`text-sm mt-1 ${isWordLimitExceeded ? 'text-red-500' : 'text-gray-500'}`}>
                      {wordCount} / 100 words {isWordLimitExceeded && '(Limit exceeded!)'}
                    </p>
                  </div>

                  {/* Full Name */}
                  <div>
                    <Label htmlFor="full_name" className="text-base font-semibold">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      placeholder="Dr. John Doe"
                      className="mt-2"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-base font-semibold">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your.email@example.com"
                      className="mt-2"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your email will not be displayed publicly
                    </p>
                  </div>

                  {/* Country */}
                  <div>
                    <Label htmlFor="country" className="text-base font-semibold">
                      Country <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="country"
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      placeholder="United Kingdom"
                      className="mt-2"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={submitting || isWordLimitExceeded}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                  >
                    {submitting ? "Submitting..." : "Submit Testimonial"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="bg-blue-50 rounded-lg p-8 max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Start Your Research Journey?
              </h3>
              <p className="text-gray-700 mb-6">
                Join our satisfied clients and experience expert research support tailored to your needs.
              </p>
              <Button
                onClick={() => navigate("/submit/general-enquiry")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
              >
                Get in Touch
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">
            © 2026 EviDent Research Consultancy Ltd. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TestimonialsPage;
